import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Icon from './AppIcon';
import Button from './ui/Button';
import MonthlyUpdatesModal from './MonthlyUpdatesModal';

const A2P_REMIND_LATER_KEY = 'a2p_remind_later_session';
const UPDATES_PROMPT_DAYS = 30;

function shouldShowMonthlyUpdatesPrompt(userProfile) {
  if (!userProfile) return false;
  const last = userProfile.last_updates_prompt_at;
  if (!last) return true;
  const at = new Date(last).getTime();
  if (Number.isNaN(at)) return true;
  const ageMs = Date.now() - at;
  return ageMs >= UPDATES_PROMPT_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * Global modals component that renders across all pages
 * Handles A2P compliance reminder, monthly life-update prompt, and other global modals
 */
const GlobalModals = () => {
  const { user, userProfile, reloadProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showA2pModal, setShowA2pModal] = useState(false);
  const [showUpdatesModal, setShowUpdatesModal] = useState(false);

  const a2pApproved = userProfile?.a2p_status === 'approved' || userProfile?.campaign_status === 'VERIFIED';
  const subscribed =
    userProfile?.subscription_status === 'active' || userProfile?.subscription_status === 'trialing';
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';
  const isOnboarding = location.pathname === '/register';
  const isSubscribePage = location.pathname === '/subscribe';

  // Show A2P modal only after subscription (A2P fees rebill via Stripe)
  useEffect(() => {
    if (!user?.id || !userProfile || a2pApproved || !subscribed || isAuthPage || isOnboarding) {
      setShowA2pModal(false);
      return;
    }
    const remindLater = sessionStorage.getItem(A2P_REMIND_LATER_KEY);
    if (remindLater) {
      setShowA2pModal(false);
      return;
    }
    setShowA2pModal(true);
  }, [user?.id, userProfile, a2pApproved, subscribed, isAuthPage, isOnboarding]);

  // Monthly life-update prompt — after A2P, only for subscribed agents, ~monthly
  useEffect(() => {
    if (
      !user?.id ||
      !userProfile ||
      !subscribed ||
      isAuthPage ||
      isOnboarding ||
      isSubscribePage ||
      showA2pModal
    ) {
      setShowUpdatesModal(false);
      return;
    }
    setShowUpdatesModal(shouldShowMonthlyUpdatesPrompt(userProfile));
  }, [
    user?.id,
    userProfile,
    subscribed,
    isAuthPage,
    isOnboarding,
    isSubscribePage,
    showA2pModal,
  ]);

  const handleA2pGoToSettings = () => {
    setShowA2pModal(false);
    navigate('/settings?tab=ai-compliance');
  };

  const handleA2pRemindLater = () => {
    setShowA2pModal(false);
    sessionStorage.setItem(A2P_REMIND_LATER_KEY, 'true');
  };

  const handleUpdatesClose = () => {
    setShowUpdatesModal(false);
    reloadProfile?.();
  };

  return (
    <>
      {/* A2P Compliance required modal - shows every time when not approved (unless Remind me later this session) */}
      {showA2pModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-xl shadow-xl max-w-md w-full p-6 animate-scale-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-destructive/15 flex items-center justify-center">
                <Icon name="Shield" size={24} className="text-destructive" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Complete AI Compliance</h2>
                <p className="text-sm text-muted-foreground">Required to send messages</p>
              </div>
            </div>
            <p className="text-sm text-foreground/90 mb-6">
              One quick form in Settings gets you approved. We handle the rest. Required before you can send SMS.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={handleA2pGoToSettings} className="flex-1">
                Go to Settings
              </Button>
              <Button variant="outline" onClick={handleA2pRemindLater} className="flex-1">
                Remind me later
              </Button>
            </div>
          </div>
        </div>
      )}

      {showUpdatesModal && !showA2pModal && (
        <MonthlyUpdatesModal onClose={handleUpdatesClose} />
      )}
    </>
  );
};

export default GlobalModals;
